import { useRef, useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useConfirmDelete } from '../hooks/useConfirmDelete'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle2, ImageOff, RotateCcw, Upload, Mail, Car, HardHat,
  Database, HardDrive, Shield, Clock, Loader2, Trash2, Download, AlertTriangle,
  Plus, FileSpreadsheet, X, MessageCircle, Pencil,
} from 'lucide-react'
import { impostazioniApi, configApi, trasfertaApi, backupApi, assegnazioniCantiereApi, authApi } from '../services/api'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import clsx from 'clsx'

interface LogoConfig {
  tipo: string
  label: string
  has_custom: boolean
  preview_url: string
}

interface BackupRecord {
  id: number
  tipo: string
  filename: string
  size_bytes: number
  size_human: string
  stato: string
  note: string | null
  creato_da: string
  created_at: string | null
}

function LogoCard({
  logo, onUpload, onDelete, uploading, justSaved,
}: {
  logo: LogoConfig; onUpload: (file: File) => void; onDelete: () => void
  uploading: boolean; justSaved: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [imgError, setImgError] = useState(false)

  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 20, background: '#fff', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#1E3A5F' }}>{logo.label}</div>
          <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
            {logo.has_custom ? 'Logo personalizzato attivo' : 'Logo predefinito'}
          </div>
        </div>
        {justSaved && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#16A34A', fontSize: 12 }}>
            <CheckCircle2 size={14} /> Salvato
          </span>
        )}
      </div>
      <div style={{ height: 90, background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {imgError ? (
          <div style={{ color: '#94A3B8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <ImageOff size={22} /><span style={{ fontSize: 11 }}>Anteprima non disponibile</span>
          </div>
        ) : (
          <img key={logo.has_custom ? 'custom' : 'default'} src={`/api${logo.preview_url}`} alt={logo.label}
            style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }}
            onError={() => setImgError(true)} onLoad={() => setImgError(false)} />
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = '' }} />
        <button className="btn-primary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13 }}
          onClick={() => inputRef.current?.click()} disabled={uploading}>
          <Upload size={14} />{uploading ? 'Caricamento…' : 'Carica logo'}
        </button>
        {logo.has_custom && (
          <button style={{ padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#64748B', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}
            onClick={onDelete} title="Ripristina logo predefinito">
            <RotateCcw size={14} /> Default
          </button>
        )}
      </div>
    </div>
  )
}

function GeneraleSection() {
  const qc = useQueryClient()
  const [uploading, setUploading] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState<string | null>(null)
  const [emailVerbali, setEmailVerbali] = useState('')
  const [emailLoaded, setEmailLoaded] = useState(false)
  const [waRapportini, setWaRapportini] = useState('')
  const [waRapportiniLoaded, setWaRapportiniLoaded] = useState(false)
  const [emailTrasferte, setEmailTrasferte] = useState('')
  const [emailTrasferteLoaded, setEmailTrasferteLoaded] = useState(false)
  const [waReferente, setWaReferente] = useState('')
  const [emailEventiTrasferte, setEmailEventiTrasferte] = useState('')
  const [waEventiTrasferte, setWaEventiTrasferte] = useState('')
  const [emailRapportinoCantieri, setEmailRapportinoCantieri] = useState('')
  const [emailDanniCantieri, setEmailDanniCantieri] = useState('')
  const [assegnazioniImpLoaded, setAssegnazioniImpLoaded] = useState(false)
  const [diarie, setDiarie] = useState({
    diaria_nord_intera_pranzo: 20, diaria_nord_intera_cena: 20,
    diaria_nord_mezza_pranzo:  20, diaria_nord_mezza_cena:   0,
    diaria_sud_intera_pranzo:  15, diaria_sud_intera_cena:  15,
    diaria_sud_mezza_pranzo:   10, diaria_sud_mezza_cena:    0,
  })
  const [diarieLoaded, setDiarieLoaded] = useState(false)

  const { data: loghi = [], isLoading } = useQuery<LogoConfig[]>({
    queryKey: ['impostazioni-loghi'],
    queryFn: () => impostazioniApi.getLoghi().then((r) => r.data),
  })

  const { data: notificheData } = useQuery({
    queryKey: ['config-notifiche'],
    queryFn: () => configApi.getNotifiche().then(r => r.data),
  })
  useEffect(() => {
    if (notificheData && !emailLoaded) { setEmailVerbali((notificheData as any).email_verbali_firmati || ''); setEmailLoaded(true) }
  }, [notificheData, emailLoaded])

  const { data: waRapportiniData } = useQuery({
    queryKey: ['config-whatsapp-rapportini'],
    queryFn: () => configApi.getWhatsappRapportini().then(r => r.data),
  })
  useEffect(() => {
    if (waRapportiniData && !waRapportiniLoaded) { setWaRapportini((waRapportiniData as any).destinatari || ''); setWaRapportiniLoaded(true) }
  }, [waRapportiniData, waRapportiniLoaded])

  const { data: trasferteImpData } = useQuery({
    queryKey: ['trasferte-impostazioni'],
    queryFn: () => trasfertaApi.impostazioni().then(r => r.data),
  })
  useEffect(() => {
    if (trasferteImpData && !emailTrasferteLoaded) {
      const d = trasferteImpData as any
      setEmailTrasferte(d.email_destinazione || '')
      setWaReferente(d.whatsapp_referente || '')
      setEmailEventiTrasferte(d.email_notifiche_eventi || '')
      setWaEventiTrasferte(d.whatsapp_notifiche_eventi || '')
      setEmailTrasferteLoaded(true)
    }
  }, [trasferteImpData, emailTrasferteLoaded])

  const { data: assegnazioniImpData } = useQuery({
    queryKey: ['assegnazioni-impostazioni'],
    queryFn: () => assegnazioniCantiereApi.impostazioni().then(r => r.data),
  })
  useEffect(() => {
    if (assegnazioniImpData && !assegnazioniImpLoaded) {
      const d = assegnazioniImpData as any
      setEmailRapportinoCantieri(d.email_rapportino || '')
      setEmailDanniCantieri(d.email_danni || '')
      setAssegnazioniImpLoaded(true)
    }
  }, [assegnazioniImpData, assegnazioniImpLoaded])

  const { data: diarieData } = useQuery({
    queryKey: ['config-diarie'],
    queryFn: () => configApi.getDiarie().then(r => r.data),
  })
  useEffect(() => {
    if (diarieData && !diarieLoaded) {
      const d = diarieData as any
      setDiarie({
        diaria_nord_intera_pranzo: d.diaria_nord_intera_pranzo ?? 20, diaria_nord_intera_cena: d.diaria_nord_intera_cena ?? 20,
        diaria_nord_mezza_pranzo: d.diaria_nord_mezza_pranzo ?? 20, diaria_nord_mezza_cena: d.diaria_nord_mezza_cena ?? 0,
        diaria_sud_intera_pranzo: d.diaria_sud_intera_pranzo ?? 15, diaria_sud_intera_cena: d.diaria_sud_intera_cena ?? 15,
        diaria_sud_mezza_pranzo: d.diaria_sud_mezza_pranzo ?? 10, diaria_sud_mezza_cena: d.diaria_sud_mezza_cena ?? 0,
      })
      setDiarieLoaded(true)
    }
  }, [diarieData, diarieLoaded])

  const uploadMut = useMutation({
    mutationFn: ({ tipo, file }: { tipo: string; file: File }) => impostazioniApi.uploadLogo(tipo, file),
    onSuccess: (_, { tipo }) => {
      qc.invalidateQueries({ queryKey: ['impostazioni-loghi'] })
      setJustSaved(tipo); setTimeout(() => setJustSaved(null), 3000)
    },
    onSettled: () => setUploading(null),
  })

  const deleteMut = useMutation({
    mutationFn: (tipo: string) => impostazioniApi.deleteLogo(tipo),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['impostazioni-loghi'] }),
  })

  const saveEmailMut = useMutation({
    mutationFn: () => configApi.updateNotifiche({ email_verbali_firmati: emailVerbali.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['config-notifiche'] }); toast.success('Destinatario aggiornato') },
    onError: () => toast.error('Errore salvataggio'),
  })

  const saveWaRapportiniMut = useMutation({
    mutationFn: () => configApi.updateWhatsappRapportini({ destinatari: waRapportini.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['config-whatsapp-rapportini'] }); toast.success('Destinatari WhatsApp aggiornati') },
    onError: () => toast.error('Errore salvataggio'),
  })

  const saveEmailTrasf = useMutation({
    mutationFn: () => trasfertaApi.salvaImpostazioni(
      emailTrasferte.trim(),
      waReferente.trim() || undefined,
      emailEventiTrasferte.trim() || undefined,
      waEventiTrasferte.trim() || undefined,
    ),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['trasferte-impostazioni'] }); toast.success('Impostazioni trasferte aggiornate') },
    onError: () => toast.error('Errore salvataggio'),
  })

  const saveEmailAssegnazioni = useMutation({
    mutationFn: () => assegnazioniCantiereApi.salvaImpostazioni(emailRapportinoCantieri.trim(), emailDanniCantieri.trim()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['assegnazioni-impostazioni'] }); toast.success('Impostazioni assegnazioni cantieri aggiornate') },
    onError: () => toast.error('Errore salvataggio'),
  })

  const saveDiarieMut = useMutation({
    mutationFn: () => configApi.saveDiarie({ ...diarie }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['config-diarie'] }); toast.success('Tariffe diaria aggiornate') },
    onError: () => toast.error('Errore salvataggio'),
  })

  return (
    <div className="space-y-6">
      {/* Notifiche email */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: '20px 24px' }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Mail size={16} color="#1E40AF" /> Notifiche email
        </h2>
        <p style={{ fontSize: 13, color: '#94A3B8', marginBottom: 20 }}>
          Configura i destinatari per le email automatiche generate dall'applicazione.
        </p>
        <div style={{ maxWidth: 480 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            Destinatario verbali firmati
          </label>
          <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 8 }}>
            Indirizzo email che riceve la copia PDF ogni volta che un tecnico firma un verbale di assegnazione beni.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="email" className="input" style={{ flex: 1 }} placeholder="assegnazioni@deltaict.it"
              value={emailVerbali} onChange={e => setEmailVerbali(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveEmailMut.mutate() }} />
            <button className="btn-primary" style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
              onClick={() => saveEmailMut.mutate()} disabled={saveEmailMut.isPending}>
              <CheckCircle2 size={14} />{saveEmailMut.isPending ? 'Salvo…' : 'Salva'}
            </button>
          </div>
        </div>
      </div>

      {/* WhatsApp rapportini */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: '20px 24px' }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
          <MessageCircle size={16} color="#1E40AF" /> WhatsApp rapportini
        </h2>
        <p style={{ fontSize: 13, color: '#94A3B8', marginBottom: 20 }}>
          Numeri di telefono e/o gruppi WhatsApp a cui viene inviato automaticamente il PDF del rapportino ad ogni invio (sia dal flusso completo da campo, sia dal bottone "Invia" della pagina Rapportini).
        </p>
        <div style={{ maxWidth: 480 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            Numeri / gruppi destinatari
          </label>
          <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 8 }}>
            Elenco separato da virgole: numeri di telefono (es. <code>393331234567</code>, senza +) e/o JID di gruppo (formato <code>1234567890@g.us</code>, visibile in "WhatsApp {'>'} Contatti").
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" className="input" style={{ flex: 1 }} placeholder="393331234567,120363426018245466@g.us"
              value={waRapportini} onChange={e => setWaRapportini(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveWaRapportiniMut.mutate() }} />
            <button className="btn-primary" style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
              onClick={() => saveWaRapportiniMut.mutate()} disabled={saveWaRapportiniMut.isPending}>
              <CheckCircle2 size={14} />{saveWaRapportiniMut.isPending ? 'Salvo…' : 'Salva'}
            </button>
          </div>
        </div>
      </div>

      {/* Trasferte */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: '20px 24px' }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Car size={16} color="#1E40AF" /> Trasferte
        </h2>
        <p style={{ fontSize: 13, color: '#94A3B8', marginBottom: 20 }}>
          Email che riceve notifiche, PDF allegati e link conferma bonifici per le richieste di trasferta.
          Riceverà anche le email di escalation ogni ora (lun–ven 09:00–18:00) se i bonifici non vengono confermati prima della data di partenza.
        </p>
        <div style={{ maxWidth: 480 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            Email destinazione trasferte
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="email" className="input" style={{ flex: 1 }} placeholder="trasferte@deltaict.it"
              value={emailTrasferte} onChange={e => setEmailTrasferte(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveEmailTrasf.mutate() }} />
          </div>
          <div style={{ marginTop: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Numero WhatsApp referente trasferte
            </label>
            <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 8 }}>
              Riceve una notifica WhatsApp ad ogni trasferta inviata (es. <code>393331234567</code> — solo cifre, senza +).
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="tel" className="input" style={{ flex: 1 }} placeholder="393331234567"
                value={waReferente} onChange={e => setWaReferente(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => { if (e.key === 'Enter') saveEmailTrasf.mutate() }} />
            </div>
          </div>

          <div style={{ marginTop: 20, borderTop: '1px solid #F3F4F6', paddingTop: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Notifiche eventi trasferta
            </label>
            <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 12 }}>
              Email e numero WhatsApp che ricevono una notifica dedicata quando una richiesta viene caricata, quando una trasferta viene completata (tutti i bonifici confermati) e quando viene caricata una fattura di cortesia. Indipendenti dai destinatari sopra.
            </p>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Email notifiche eventi
            </label>
            <input type="email" className="input" style={{ width: '100%' }} placeholder="eventi@deltaict.it"
              value={emailEventiTrasferte} onChange={e => setEmailEventiTrasferte(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveEmailTrasf.mutate() }} />
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginTop: 14, marginBottom: 6 }}>
              WhatsApp notifiche eventi
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="tel" className="input" style={{ flex: 1 }} placeholder="393331234567"
                value={waEventiTrasferte} onChange={e => setWaEventiTrasferte(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => { if (e.key === 'Enter') saveEmailTrasf.mutate() }} />
              <button className="btn-primary" style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                onClick={() => saveEmailTrasf.mutate()} disabled={saveEmailTrasf.isPending}>
                <CheckCircle2 size={14} />{saveEmailTrasf.isPending ? 'Salvo…' : 'Salva tutto'}
              </button>
            </div>
          </div>

          <p style={{ fontSize: 12, color: '#94A3B8', marginTop: 12 }}>
            Admin e superadmin vedono sempre tutte le trasferte. Per abilitare il modulo ad altri utenti, usa la sezione <strong>Permessi Menu</strong> nella pagina Utenti.
          </p>
        </div>

        <div style={{ marginTop: 20, borderTop: '1px solid #F3F4F6', paddingTop: 20 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Tariffe diaria giornaliera (€/operatore/giorno)
          </label>
          <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 14 }}>
            Ogni diaria è la somma di pranzo + cena. Il totale viene usato nel calcolo automatico delle trasferte.
          </p>

          {([
            {
              zona: 'Nord Italia',
              righe: [
                { label: 'Intera giornata', pk: 'diaria_nord_intera_pranzo' as keyof typeof diarie, ck: 'diaria_nord_intera_cena' as keyof typeof diarie },
                { label: 'Mezza pensione',  pk: 'diaria_nord_mezza_pranzo'  as keyof typeof diarie, ck: 'diaria_nord_mezza_cena'  as keyof typeof diarie },
              ],
            },
            {
              zona: 'Centro / Sud Italia',
              righe: [
                { label: 'Intera giornata', pk: 'diaria_sud_intera_pranzo' as keyof typeof diarie, ck: 'diaria_sud_intera_cena' as keyof typeof diarie },
                { label: 'Mezza pensione',  pk: 'diaria_sud_mezza_pranzo'  as keyof typeof diarie, ck: 'diaria_sud_mezza_cena'  as keyof typeof diarie },
              ],
            },
          ]).map(({ zona, righe }) => (
            <div key={zona} style={{ marginBottom: 16, border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ background: '#F8FAFC', padding: '8px 14px', fontSize: 12, fontWeight: 700, color: '#1E40AF', borderBottom: '1px solid #E5E7EB', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {zona}
              </div>
              <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {righe.map(({ label, pk, ck }) => {
                  const totale = (diarie[pk] || 0) + (diarie[ck] || 0)
                  return (
                    <div key={pk} style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr 1fr', gap: 10, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>{label}</span>
                      <div>
                        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 3 }}>Pranzo</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <input type="number" min={0} step={0.5} className="input" style={{ width: 72 }}
                            value={diarie[pk]}
                            onChange={e => setDiarie(prev => ({ ...prev, [pk]: parseFloat(e.target.value) || 0 }))} />
                          <span style={{ fontSize: 12, color: '#6B7280' }}>€</span>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 3 }}>Cena</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <input type="number" min={0} step={0.5} className="input" style={{ width: 72 }}
                            value={diarie[ck]}
                            onChange={e => setDiarie(prev => ({ ...prev, [ck]: parseFloat(e.target.value) || 0 }))} />
                          <span style={{ fontSize: 12, color: '#6B7280' }}>€</span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 3 }}>Totale</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#15803D' }}>€{totale.toFixed(2)}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}

          <button
            className="btn-primary"
            style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
            onClick={() => saveDiarieMut.mutate()}
            disabled={saveDiarieMut.isPending}
          >
            <CheckCircle2 size={14} />{saveDiarieMut.isPending ? 'Salvo…' : 'Salva tariffe'}
          </button>
        </div>
      </div>

      {/* Assegnazioni Cantieri */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: '20px 24px' }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
          <HardHat size={16} color="#1E40AF" /> Assegnazioni Cantieri
        </h2>
        <p style={{ fontSize: 13, color: '#94A3B8', marginBottom: 20 }}>
          Destinatari delle email automatiche generate dal modulo Assegnazioni Cantieri. Più indirizzi si separano con una virgola.
        </p>
        <div style={{ maxWidth: 480 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            Destinatario rapportino attività
          </label>
          <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 8 }}>
            Riceve il PDF del rapportino ogni volta che un tecnico termina un'attività su un'assegnazione cantiere.
          </p>
          <input type="text" className="input" style={{ width: '100%' }} placeholder="dispatcher@deltaict.it, capo.cantiere@deltaict.it"
            value={emailRapportinoCantieri} onChange={e => setEmailRapportinoCantieri(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveEmailAssegnazioni.mutate() }} />

          <div style={{ marginTop: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Destinatario danni segnalati
            </label>
            <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 8 }}>
              Riceve la segnalazione quando un tecnico registra danni alla presa in consegna o alla riconsegna di un mezzo.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="text" className="input" style={{ flex: 1 }} placeholder="wfm@deltaict.it"
                value={emailDanniCantieri} onChange={e => setEmailDanniCantieri(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveEmailAssegnazioni.mutate() }} />
              <button className="btn-primary" style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                onClick={() => saveEmailAssegnazioni.mutate()} disabled={saveEmailAssegnazioni.isPending}>
                <CheckCircle2 size={14} />{saveEmailAssegnazioni.isPending ? 'Salvo…' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loghi */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', padding: '20px 24px' }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
          Loghi modelli di stampa
        </h2>
        <p style={{ fontSize: 13, color: '#94A3B8', marginBottom: 20 }}>
          Carica un'immagine (PNG o JPG) per sostituire il logo predefinito su ciascun tipo di documento.
          Il logo viene applicato immediatamente alle nuove stampe.
        </p>
        {isLoading ? (
          <p style={{ color: '#94A3B8', fontSize: 13 }}>Caricamento…</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {loghi.map((logo) => (
              <LogoCard key={logo.tipo} logo={logo} uploading={uploading === logo.tipo} justSaved={justSaved === logo.tipo}
                onUpload={(file) => { setUploading(logo.tipo); uploadMut.mutate({ tipo: logo.tipo, file }) }}
                onDelete={() => deleteMut.mutate(logo.tipo)} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function SmtpSection() {
  const [smtpForm, setSmtpForm] = useState({
    smtp_host: '', smtp_port: '465', smtp_user: '',
    smtp_password: '', smtp_ssl: 'true',
    email_mittente_nome: '', email_test: '',
  })
  const [loaded, setLoaded] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null)

  useEffect(() => {
    if (!loaded) {
      configApi.getSmtp().then(r => { setSmtpForm(prev => ({ ...prev, ...r.data })); setLoaded(true) }).catch(() => {})
    }
  }, [loaded])

  const saveSMTP = useMutation({
    mutationFn: () => configApi.updateSmtp(smtpForm),
    onSuccess: () => toast.success('Impostazioni email salvate'),
    onError: (e: any) => toast.error('Errore: ' + (e.response?.data?.detail || 'sconosciuto')),
  })

  const testSMTP = useMutation({
    mutationFn: () => configApi.testSmtp(smtpForm),
    onSuccess: (r: any) => setTestResult({ ok: true, msg: r.data.messaggio }),
    onError: (e: any) => setTestResult({ ok: false, msg: e.response?.data?.detail || 'Errore connessione' }),
  })

  return (
    <div className="card p-6 max-w-xl space-y-4">
      <h2 className="font-semibold text-slate-800 flex items-center gap-2">
        <Mail size={18} className="text-blue-600" /> Configurazione Email SMTP
      </h2>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Server SMTP (Host)</label>
          <input className="input" placeholder="smtps.aruba.it" value={smtpForm.smtp_host}
            onChange={e => setSmtpForm(p => ({ ...p, smtp_host: e.target.value }))} />
        </div>
        <div>
          <label className="label">Porta</label>
          <input className="input" placeholder="465" value={smtpForm.smtp_port}
            onChange={e => setSmtpForm(p => ({ ...p, smtp_port: e.target.value }))} />
        </div>
      </div>
      <div>
        <label className="label">Email mittente (username SMTP)</label>
        <input className="input" type="email" placeholder="wfm@deltaict.it" value={smtpForm.smtp_user}
          onChange={e => setSmtpForm(p => ({ ...p, smtp_user: e.target.value }))} />
      </div>
      <div>
        <label className="label">Password SMTP</label>
        <input className="input" type="password" placeholder="lascia *** per non modificare" value={smtpForm.smtp_password}
          onChange={e => setSmtpForm(p => ({ ...p, smtp_password: e.target.value }))} />
      </div>
      <div>
        <label className="label">Nome mittente visualizzato</label>
        <input className="input" placeholder="X-Sophia 360" value={smtpForm.email_mittente_nome}
          onChange={e => setSmtpForm(p => ({ ...p, email_mittente_nome: e.target.value }))} />
      </div>
      <div>
        <label className="label">SSL/TLS</label>
        <select className="select" value={smtpForm.smtp_ssl}
          onChange={e => setSmtpForm(p => ({ ...p, smtp_ssl: e.target.value }))}>
          <option value="true">SSL (porta 465) — raccomandato Aruba</option>
          <option value="false">STARTTLS (porta 587)</option>
        </select>
      </div>
      <div className="border-t pt-4 space-y-3">
        <div>
          <label className="label">Email per test (vuoto = usa il mittente)</label>
          <input className="input" type="email" placeholder="tua@email.it" value={smtpForm.email_test}
            onChange={e => setSmtpForm(p => ({ ...p, email_test: e.target.value }))} />
        </div>
        {testResult && (
          <div style={{ padding: '10px 14px', borderRadius: 10, fontSize: 13,
            background: testResult.ok ? '#f0fdf4' : '#fef2f2',
            color: testResult.ok ? '#16a34a' : '#b91c1c',
            border: `1px solid ${testResult.ok ? '#bbf7d0' : '#fecaca'}` }}>
            {testResult.ok ? '✅ ' : '❌ '}{testResult.msg}
          </div>
        )}
        <div className="flex gap-3">
          <button onClick={() => { setTestResult(null); testSMTP.mutate() }} disabled={testSMTP.isPending} className="btn-secondary flex-1">
            {testSMTP.isPending ? '📨 Invio...' : '📨 Invia Email di Test'}
          </button>
          <button onClick={() => saveSMTP.mutate()} disabled={saveSMTP.isPending} className="btn-primary flex-1">
            {saveSMTP.isPending ? 'Salvataggio...' : '💾 Salva Impostazioni'}
          </button>
        </div>
      </div>
    </div>
  )
}

function GoogleDriveSection() {
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: cfg, isLoading } = useQuery<any>({
    queryKey: ['gdrive-config'],
    queryFn: () => backupApi.gdriveConfig().then(r => r.data),
  })
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [guida, setGuida] = useState(false)
  const [metodo, setMetodo] = useState<'rclone' | 'app'>('rclone')
  const [codiceRclone, setCodiceRclone] = useState('')
  const [cartellaRclone, setCartellaRclone] = useState('')
  useEffect(() => { if (cfg) setClientId(cfg.client_id || '') }, [cfg])
  const rcloneMut = useMutation({
    mutationFn: () => backupApi.gdriveTokenRclone({ codice: codiceRclone, cartella: cartellaRclone || undefined }),
    onSuccess: (r) => {
      toast.success(`Google Drive collegato${r.data.account_email ? ': ' + r.data.account_email : ''}`)
      setCodiceRclone(''); setCartellaRclone('')
      qc.invalidateQueries({ queryKey: ['gdrive-config'] })
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Collegamento non riuscito', { duration: 9000 }),
  })

  // Esito del ritorno da Google (redirect su /impostazioni?tab=backup&gdrive=ok|errore&msg=...)
  useEffect(() => {
    const esito = searchParams.get('gdrive')
    if (!esito) return
    const msg = searchParams.get('msg') || ''
    if (esito === 'ok') toast.success(`Google Drive collegato${msg ? ': ' + msg : ''}`)
    else toast.error(msg || 'Collegamento a Google Drive non riuscito', { duration: 8000 })
    const p = new URLSearchParams(searchParams); p.delete('gdrive'); p.delete('msg')
    setSearchParams(p, { replace: true })
    qc.invalidateQueries({ queryKey: ['gdrive-config'] })
  }, [searchParams])

  const salvaMut = useMutation({
    mutationFn: () => backupApi.gdriveSalvaConfig({ client_id: clientId, client_secret: clientSecret || undefined }),
    onSuccess: () => { toast.success('Credenziali salvate'); setClientSecret(''); qc.invalidateQueries({ queryKey: ['gdrive-config'] }) },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore salvataggio'),
  })
  const collegaMut = useMutation({
    mutationFn: () => backupApi.gdriveAuthorizeUrl().then(r => r.data.url as string),
    onSuccess: (url) => { window.location.href = url },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Errore avvio collegamento'),
  })
  const testMut = useMutation({
    mutationFn: () => backupApi.gdriveTest(),
    onSuccess: () => { toast.success('Caricamento di prova riuscito: Google Drive funziona'); qc.invalidateQueries({ queryKey: ['gdrive-config'] }) },
    onError: (e: any) => { toast.error(e.response?.data?.detail || 'Caricamento di prova fallito', { duration: 8000 }); qc.invalidateQueries({ queryKey: ['gdrive-config'] }) },
  })
  const scollegaMut = useMutation({
    mutationFn: () => backupApi.gdriveDisconnect(),
    onSuccess: () => { toast.success('Account scollegato'); qc.invalidateQueries({ queryKey: ['gdrive-config'] }) },
  })

  if (isLoading || !cfg) return <div className="card" style={{ padding: 20, color: '#94a3b8' }}>Caricamento configurazione Google Drive...</div>
  const fmt = (iso?: string | null) => iso ? format(new Date(iso), 'dd/MM/yyyy HH:mm', { locale: it }) : '—'
  const statoColore = cfg.attivo && !cfg.ultimo_errore ? '#15803d' : cfg.collegato ? '#f59e0b' : '#dc2626'
  const statoTesto = cfg.attivo && !cfg.ultimo_errore ? 'Collegato e attivo' : cfg.collegato ? 'Collegato, ultimo caricamento con errore' : 'Non collegato: i backup restano solo sul server'

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 600, color: '#1e293b' }}>Copia su Google Drive</div>
          <div style={{ fontSize: 13, color: '#64748b' }}>Ogni backup viene copiato automaticamente su Google Drive (si conservano gli ultimi 7).</div>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, color: statoColore, background: `${statoColore}14` }}>
          ● {statoTesto}
        </span>
      </div>

      {cfg.collegato && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, fontSize: 13, marginBottom: 14, background: '#f8fafc', padding: 12, borderRadius: 10 }}>
          <div><div className="label">Account Google</div>{cfg.account_email || '—'}</div>
          <div><div className="label">Cartella</div>{cfg.folder_url ? <a href={cfg.folder_url} target="_blank" rel="noreferrer" className="text-blue-600">XSophia Backup ↗</a> : '—'}</div>
          <div><div className="label">Ultimo caricamento</div>{fmt(cfg.ultimo_upload)}</div>
          {cfg.ultimo_errore && <div style={{ gridColumn: '1 / -1', color: '#b91c1c' }}><div className="label">Ultimo errore</div>{cfg.ultimo_errore}</div>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {([['rclone', 'Collega con rclone (consigliato)'], ['app', 'Collega con app Google Cloud']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setMetodo(k)} className={metodo === k ? 'btn-primary text-xs' : 'btn-secondary text-xs'}>{l}</button>
        ))}
      </div>

      {metodo === 'rclone' && (
        <div>
          <ol style={{ fontSize: 13, color: '#334155', paddingLeft: 18, lineHeight: 1.7, listStyle: 'decimal', marginBottom: 10 }}>
            <li>Su un PC Windows con browser scarica rclone da <a className="text-blue-600" href="https://rclone.org/downloads/" target="_blank" rel="noreferrer">rclone.org/downloads</a> (Windows, Intel/AMD 64 bit) ed estrai lo zip.</li>
            <li>Apri la cartella estratta, scrivi <code>cmd</code> nella barra dell'indirizzo e premi Invio.</li>
            <li>Nella finestra nera scrivi <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>rclone authorize "drive"</code> e premi Invio.</li>
            <li>Si apre il browser: accedi con l'account Google dei backup e premi <b>Consenti</b>.</li>
            <li>Torna nella finestra nera: copia tutto il testo tra <code>---&gt;</code> e <code>&lt;---End paste</code> e incollalo qui sotto.</li>
          </ol>
          <label className="label">Codice ottenuto da rclone</label>
          <textarea className="input font-mono text-xs" rows={3} value={codiceRclone} onChange={e => setCodiceRclone(e.target.value)}
            placeholder='{"access_token":"...","token_type":"Bearer","refresh_token":"...","expiry":"..."}' />
          <label className="label" style={{ marginTop: 8 }}>Cartella Drive (facoltativa)</label>
          <input className="input text-xs" value={cartellaRclone} onChange={e => setCartellaRclone(e.target.value)}
            placeholder='Lascia vuoto per creare "XSophia Backup", oppure incolla il link di una cartella esistente' />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <button className="btn-primary" disabled={rcloneMut.isPending || !codiceRclone.trim()} onClick={() => rcloneMut.mutate()}>
              {rcloneMut.isPending ? 'Collegamento...' : 'Collega Google Drive'}
            </button>
            {cfg.collegato && (
              <>
                <button className="btn-secondary" disabled={testMut.isPending} onClick={() => testMut.mutate()}>
                  {testMut.isPending ? 'Prova in corso...' : 'Prova caricamento'}
                </button>
                <button className="btn-ghost text-red-600" onClick={() => { if (confirm('Scollegare l\'account Google? I backup resteranno solo sul server.')) scollegaMut.mutate() }}>
                  Scollega
                </button>
              </>
            )}
          </div>
          <p style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>Il codice è una credenziale: incollalo solo qui. La password Google resta sulla pagina di Google.</p>
        </div>
      )}

      {metodo === 'app' && <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
        <div>
          <label className="label">Client ID Google</label>
          <input className="input font-mono text-xs" value={clientId} onChange={e => setClientId(e.target.value)} placeholder="xxxxxxxx.apps.googleusercontent.com" />
        </div>
        <div>
          <label className="label">Client Secret</label>
          <input className="input font-mono text-xs" type="password" autoComplete="off" value={clientSecret} onChange={e => setClientSecret(e.target.value)}
            placeholder={cfg.client_secret_impostato ? '•••••••• (salvato — lascia vuoto per non cambiarlo)' : 'GOCSPX-...'} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
        <button className="btn-secondary" disabled={salvaMut.isPending || !clientId.trim()} onClick={() => salvaMut.mutate()}>
          {salvaMut.isPending ? 'Salvataggio...' : 'Salva credenziali'}
        </button>
        <button className="btn-primary" disabled={collegaMut.isPending || !cfg.client_id || !cfg.client_secret_impostato} onClick={() => collegaMut.mutate()}
          title={!cfg.client_id || !cfg.client_secret_impostato ? 'Salva prima Client ID e Client Secret' : ''}>
          {cfg.collegato ? 'Ricollega account Google' : 'Collega account Google'}
        </button>
        {cfg.collegato && (
          <>
            <button className="btn-secondary" disabled={testMut.isPending} onClick={() => testMut.mutate()}>
              {testMut.isPending ? 'Prova in corso...' : 'Prova caricamento'}
            </button>
            <button className="btn-ghost text-red-600" onClick={() => { if (confirm('Scollegare l\'account Google? I backup resteranno solo sul server.')) scollegaMut.mutate() }}>
              Scollega
            </button>
          </>
        )}
        <button className="btn-ghost text-blue-600" onClick={() => setGuida(g => !g)}>{guida ? 'Nascondi guida' : 'Come ottengo Client ID e Secret?'}</button>
      </div>
      <p style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
        Email e password dell'account Google si inseriscono sulla pagina di accesso di Google che si apre con "Collega account Google": XSophia non le vede e non le salva.
      </p>

      {guida && (
        <ol style={{ fontSize: 13, color: '#334155', marginTop: 12, paddingLeft: 18, lineHeight: 1.7, listStyle: 'decimal' }}>
          <li>Apri <a className="text-blue-600" href="https://console.cloud.google.com/projectcreate" target="_blank" rel="noreferrer">Google Cloud Console</a> con l'account Google dei backup e crea un progetto (es. "XSophia Backup").</li>
          <li>In <b>API e servizi → Libreria</b> abilita <b>Google Drive API</b>.</li>
          <li>In <b>Schermata consenso OAuth</b> scegli tipo <b>Esterno</b>, compila nome app ed email, poi <b>Pubblica l'app</b> (stato "In produzione": così l'autorizzazione non scade dopo 7 giorni).</li>
          <li>In <b>Credenziali → Crea credenziali → ID client OAuth</b> scegli <b>Applicazione web</b> e aggiungi questo URI di reindirizzamento autorizzato:
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '4px 0' }}>
              <code style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 6, fontSize: 12 }}>{cfg.redirect_uri}</code>
              <button className="btn-ghost text-xs" onClick={() => { navigator.clipboard?.writeText(cfg.redirect_uri); toast.success('Copiato') }}>Copia</button>
            </div>
          </li>
          <li>Copia <b>Client ID</b> e <b>Client Secret</b> qui sopra, premi <b>Salva credenziali</b> e poi <b>Collega account Google</b>.</li>
          <li>Accedi con l'account Google e consenti l'accesso: XSophia crea la cartella <b>XSophia Backup</b> e vede solo i file che crea lei.</li>
        </ol>
      )}
      </>}
    </div>
  )
}

function BackupSection() {
  const qc = useQueryClient()
  const [ripristinaModal, setRipristinaModal] = useState<{ id: number; tipo: string } | null>(null)
  const [eliminaId, setEliminaId] = useState<number | null>(null)

  const { data: backups = [], isLoading } = useQuery<BackupRecord[]>({
    queryKey: ['backups'],
    queryFn: () => backupApi.lista().then(r => r.data),
    refetchInterval: 15_000,
  })

  const eseguiMut = useMutation({
    mutationFn: (tipo: 'dati' | 'completo') => backupApi.esegui(tipo),
    onSuccess: res => {
      qc.invalidateQueries({ queryKey: ['backups'] })
      const d = res.data
      if (d.stato === 'completato') toast.success(`Backup completato — ${d.size}`)
      else toast.error(`Errore backup: ${d.note}`)
    },
    onError: () => toast.error('Errore durante il backup'),
  })

  const ripristinaMut = useMutation({
    mutationFn: ({ id, cosa }: { id: number; cosa: string }) =>
      backupApi.ripristina(id, cosa as 'dati' | 'file' | 'tutto'),
    onSuccess: res => {
      setRipristinaModal(null)
      qc.invalidateQueries({ queryKey: ['backups'] })
      if (res.data.stato === 'ok') toast.success('Ripristino completato con successo')
      else toast.error(`Ripristino parziale: ${res.data.errori?.join(', ')}`)
    },
    onError: () => toast.error('Errore durante il ripristino'),
  })

  const eliminaMut = useMutation({
    mutationFn: (id: number) => backupApi.elimina(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['backups'] }); setEliminaId(null); toast.success('Backup eliminato') },
  })

  const handleDownload = async (id: number, filename: string) => {
    try {
      const res = await backupApi.download(id)
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click()
      URL.revokeObjectURL(url)
    } catch { toast.error('Errore download') }
  }

  const STATO_ICON: Record<string, JSX.Element> = {
    completato: <CheckCircle2 size={14} color="#16a34a" />,
    errore:     <AlertTriangle size={14} color="#dc2626" />,
    in_corso:   <Loader2 size={14} color="#2563eb" />,
  }

  return (
    <div className="space-y-6">
      <GoogleDriveSection />
      {/* Azioni manuali */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Database size={20} color="#2563eb" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>Backup Dati</div>
              <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5, marginBottom: 12 }}>
                Esporta solo il database PostgreSQL (.sql). Veloce — ideale per backup frequenti.
              </div>
              <button onClick={() => eseguiMut.mutate('dati')} disabled={eseguiMut.isPending}
                className="btn-primary" style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                {eseguiMut.isPending ? <Loader2 size={13} /> : <Database size={13} />}
                {eseguiMut.isPending ? 'In corso...' : 'Avvia Backup Dati'}
              </button>
            </div>
          </div>
        </div>
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#FFF7ED', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <HardDrive size={20} color="#ea580c" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>Backup Completo</div>
              <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5, marginBottom: 12 }}>
                Database + file caricati (rapportini, allegati) + configurazione Docker (.tar.gz).
              </div>
              <button onClick={() => eseguiMut.mutate('completo')} disabled={eseguiMut.isPending}
                className="btn-secondary" style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, borderColor: '#fed7aa', color: '#ea580c' }}>
                {eseguiMut.isPending ? <Loader2 size={13} /> : <HardDrive size={13} />}
                {eseguiMut.isPending ? 'In corso...' : 'Avvia Backup Completo'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Banner scheduler */}
      <div className="card" style={{ padding: '12px 16px', background: '#f0fdf4', borderColor: '#bbf7d0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Clock size={15} color="#16a34a" />
        <span style={{ fontSize: 13, color: '#166534', fontWeight: 500 }}>
          Backup automatico attivo — ogni giorno alle 00:00 viene eseguito un backup dati automatico.
        </span>
      </div>

      {/* Lista backup */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <span style={{ fontWeight: 600, color: '#1e293b' }}>Storico Backup</span>
        </div>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 48, color: '#94a3b8' }}>
            <Loader2 size={18} /> Caricamento...
          </div>
        ) : backups.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            Nessun backup disponibile. Esegui il primo backup manualmente.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                  {['Data', 'Tipo', 'File', 'Dimensione', 'Stato', 'Creato da', 'Azioni'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: h === 'Azioni' ? 'center' : 'left', fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {backups.map(b => (
                  <tr key={b.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 16px', color: '#475569', fontFamily: 'monospace', fontSize: 12 }}>
                      {b.created_at ? format(new Date(b.created_at), 'dd/MM/yyyy HH:mm', { locale: it }) : '—'}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: b.tipo === 'completo' ? '#FFF7ED' : '#EFF6FF', color: b.tipo === 'completo' ? '#ea580c' : '#2563eb' }}>
                        {b.tipo === 'completo' ? <HardDrive size={11} /> : <Database size={11} />}{b.tipo}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px', color: '#94a3b8', fontFamily: 'monospace', fontSize: 11, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.filename}</td>
                    <td style={{ padding: '10px 16px', color: '#475569', fontSize: 12 }}>{b.size_human || '—'}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: b.stato === 'completato' ? '#16a34a' : b.stato === 'errore' ? '#dc2626' : '#2563eb' }}>
                        {STATO_ICON[b.stato]}{b.stato}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px', color: '#64748b', fontSize: 12 }}>{b.creato_da}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        {b.stato === 'completato' && (
                          <>
                            <button onClick={() => setRipristinaModal({ id: b.id, tipo: b.tipo })} title="Ripristina"
                              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6, border: 'none', background: 'transparent', color: '#2563eb', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>
                              <RotateCcw size={13} /> Ripristina
                            </button>
                            <button onClick={() => handleDownload(b.id, b.filename)} title="Scarica"
                              style={{ padding: '4px 8px', borderRadius: 6, border: 'none', background: 'transparent', color: '#475569', cursor: 'pointer' }}>
                              <Download size={13} />
                            </button>
                          </>
                        )}
                        <button onClick={() => setEliminaId(b.id)} title="Elimina"
                          style={{ padding: '4px 8px', borderRadius: 6, border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Ripristina */}
      {ripristinaModal && (
        <div onClick={e => { if (e.target === e.currentTarget) setRipristinaModal(null) }}
          style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
          <div className="card" style={{ width: '100%', maxWidth: 420, padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <RotateCcw size={20} color="#dc2626" />
              </div>
              <div>
                <div style={{ fontWeight: 600, color: '#1e293b' }}>Ripristina Backup</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Scegli cosa ripristinare</div>
              </div>
            </div>
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#b91c1c', fontWeight: 500, marginBottom: 16 }}>
              ⚠ Il ripristino sovrascrive i dati attuali. Operazione irreversibile.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {[
                { cosa: 'dati', label: 'Solo Database', desc: 'Ripristina solo il database PostgreSQL', icon: <Database size={16} /> },
                ...(ripristinaModal.tipo === 'completo' ? [
                  { cosa: 'file', label: 'Solo File', desc: 'Ripristina solo i file caricati', icon: <HardDrive size={16} /> },
                  { cosa: 'tutto', label: 'Database + File', desc: 'Ripristino totale del sistema', icon: <Shield size={16} /> },
                ] : []),
              ].map(opt => (
                <button key={opt.cosa} onClick={() => ripristinaMut.mutate({ id: ripristinaModal.id, cosa: opt.cosa })}
                  disabled={ripristinaMut.isPending}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e5e7eb', background: 'white', cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ color: '#2563eb', flexShrink: 0 }}>{opt.icon}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{opt.label}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{opt.desc}</div>
                  </div>
                  {ripristinaMut.isPending && <Loader2 size={14} color="#2563eb" style={{ marginLeft: 'auto' }} />}
                </button>
              ))}
            </div>
            <button onClick={() => setRipristinaModal(null)} className="btn-secondary" style={{ width: '100%' }}>Annulla</button>
          </div>
        </div>
      )}

      {/* Modal Elimina */}
      {eliminaId !== null && (
        <div onClick={e => { if (e.target === e.currentTarget) setEliminaId(null) }}
          style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
          <div className="card" style={{ width: '100%', maxWidth: 360, padding: 24 }}>
            <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Elimina backup?</div>
            <div style={{ fontSize: 14, color: '#64748b', marginBottom: 20 }}>
              Il file verrà eliminato dal disco. Operazione irreversibile.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setEliminaId(null)} className="btn-secondary" style={{ flex: 1 }}>Annulla</button>
              <button onClick={() => eliminaMut.mutate(eliminaId)} disabled={eliminaMut.isPending}
                style={{ flex: 1, padding: '8px 16px', border: 'none', borderRadius: 8, background: '#dc2626', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                {eliminaMut.isPending ? 'Eliminazione...' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface CommessaRow { id: number; codice: string; ragione_sociale: string; label: string }

function CommesseSection() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const qc = useQueryClient()
  const importRef = useRef<HTMLInputElement>(null)
  const [nuova, setNuova] = useState({ codice: '', ragione_sociale: '' })
  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [editValues, setEditValues] = useState({ codice: '', ragione_sociale: '' })

  const { data: commesse = [], isLoading } = useQuery<CommessaRow[]>({
    queryKey: ['commesse-trasferta'],
    queryFn: () => configApi.getCommesse().then(r => r.data),
  })

  const createMut = useMutation({
    mutationFn: () => configApi.createCommessa(nuova),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commesse-trasferta'] }); setNuova({ codice: '', ragione_sociale: '' }); setShowForm(false); toast.success('Commessa aggiunta') },
    onError: () => toast.error('Errore durante la creazione'),
  })

  const updateMut = useMutation({
    mutationFn: () => configApi.updateCommessa(editId as number, editValues),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commesse-trasferta'] }); setEditId(null); toast.success('Commessa aggiornata') },
    onError: () => toast.error('Errore durante l\'aggiornamento'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => configApi.deleteCommessa(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commesse-trasferta'] }); toast.success('Eliminata') },
  })

  const deleteAllMut = useMutation({
    mutationFn: () => configApi.deleteAllCommesse(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commesse-trasferta'] }); toast.success('Elenco svuotato') },
  })

  const importMut = useMutation({
    mutationFn: (file: File) => configApi.importCommesse(file),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['commesse-trasferta'] })
      toast.success(`Importate ${r.data.importati} nuove, aggiornate ${r.data.aggiornati}`)
    },
    onError: () => toast.error('Errore importazione'),
  })

  const handleExport = async () => {
    try {
      const res = await configApi.exportCommesse()
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a'); a.href = url; a.download = 'commesse_trasferta.xlsx'; a.click()
      URL.revokeObjectURL(url)
    } catch { toast.error('Errore export') }
  }

  return (
    <div className="card" style={{ padding: 20 }}>
      {ConfirmModal}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileSpreadsheet size={16} className="text-blue-600" /> Commesse
          </h2>
          <p style={{ fontSize: 12, color: '#6B7280' }}>
            Elenco commesse disponibili come menu a tendina nelle richieste trasferta.
            Colonna A = codice, Colonna D = ragione sociale.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" style={{ fontSize: 12 }} onClick={handleExport}>
            <Download size={13} /> Scarica XLSX
          </button>
          <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => importRef.current?.click()}
            disabled={importMut.isPending}>
            <Upload size={13} /> {importMut.isPending ? 'Importo…' : 'Importa XLSX'}
          </button>
          <input ref={importRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) importMut.mutate(f); e.target.value = '' }} />
          <button className="btn-primary" style={{ fontSize: 12 }} onClick={() => setShowForm(v => !v)}>
            <Plus size={13} /> Aggiungi
          </button>
        </div>
      </div>

      {showForm && (
        <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: 16, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: '0 0 160px' }}>
            <label className="label">Codice commessa</label>
            <input className="input" placeholder="es. 26/018" value={nuova.codice}
              onChange={e => setNuova(p => ({ ...p, codice: e.target.value }))} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="label">Ragione sociale</label>
            <input className="input" placeholder="es. Università degli Studi di Padova" value={nuova.ragione_sociale}
              onChange={e => setNuova(p => ({ ...p, ragione_sociale: e.target.value }))} />
          </div>
          <button className="btn-primary" style={{ fontSize: 12 }}
            onClick={() => createMut.mutate()}
            disabled={!nuova.codice.trim() || !nuova.ragione_sociale.trim() || createMut.isPending}>
            {createMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Salva
          </button>
          <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setShowForm(false)}>
            <X size={13} />
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-400 py-4"><Loader2 size={16} className="animate-spin" /> Caricamento…</div>
      ) : commesse.length === 0 ? (
        <p style={{ color: '#9CA3AF', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
          Nessuna commessa. Importa un file XLSX o aggiungi manualmente.
        </p>
      ) : (
        <>
          <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: '#F1F5F9', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', width: 140 }}>Codice</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Ragione Sociale</th>
                  <th style={{ width: 64 }} />
                </tr>
              </thead>
              <tbody>
                {commesse.map((c, i) => {
                  const isEditing = editId === c.id
                  return (
                    <tr key={c.id} style={{ borderTop: '1px solid #F1F5F9', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                      {isEditing ? (
                        <>
                          <td style={{ padding: '4px 8px' }}>
                            <input className="input" style={{ fontFamily: 'monospace', fontSize: 13, padding: '4px 8px' }}
                              value={editValues.codice}
                              onChange={e => setEditValues(p => ({ ...p, codice: e.target.value }))} />
                          </td>
                          <td style={{ padding: '4px 8px' }}>
                            <input className="input" style={{ fontSize: 13, padding: '4px 8px' }}
                              value={editValues.ragione_sociale}
                              onChange={e => setEditValues(p => ({ ...p, ragione_sociale: e.target.value }))} />
                          </td>
                          <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                            <button onClick={() => updateMut.mutate()}
                              disabled={!editValues.codice.trim() || !editValues.ragione_sociale.trim() || updateMut.isPending}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16A34A', padding: 4 }}>
                              {updateMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                            </button>
                            <button onClick={() => setEditId(null)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 4 }}>
                              <X size={13} />
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '6px 12px', fontWeight: 600, color: '#1E40AF', fontFamily: 'monospace' }}>{c.codice}</td>
                          <td style={{ padding: '6px 12px', color: '#374151' }}>{c.ragione_sociale}</td>
                          <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                            <button onClick={() => { setEditId(c.id); setEditValues({ codice: c.codice, ragione_sociale: c.ragione_sociale }) }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 4 }}>
                              <Pencil size={13} />
                            </button>
                            <button onClick={() => confirmDelete('Eliminare?', () => deleteMut.mutate(c.id))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }}>
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <span style={{ fontSize: 12, color: '#6B7280' }}>{commesse.length} commesse</span>
            <button className="btn-ghost" style={{ fontSize: 11, color: '#EF4444' }}
              onClick={() => confirmDelete('Eliminare tutte le commesse?', () => deleteAllMut.mutate())}
              disabled={deleteAllMut.isPending}>
              <Trash2 size={11} /> Svuota elenco
            </button>
          </div>
        </>
      )}
    </div>
  )
}

type TabKey = 'generale' | 'smtp' | 'backup' | 'commesse' | 'red' | 'sicurezza'
const VALID_TABS: TabKey[] = ['generale', 'smtp', 'backup', 'commesse', 'red', 'sicurezza']

export default function Impostazioni() {
  const [searchParams] = useSearchParams()
  const tabFromUrl = searchParams.get('tab') as TabKey | null
  const [tab, setTab] = useState<TabKey>(
    tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'generale'
  )

  const TABS = [
    { key: 'generale',  label: 'Generale' },
    { key: 'smtp',      label: 'Email SMTP' },
    { key: 'backup',    label: 'Backup & Ripristino' },
    { key: 'commesse',  label: 'Commesse' },
    { key: 'red',       label: 'Modulo RED' },
    { key: 'sicurezza', label: 'Sicurezza' },
  ] as const

  return (
    <div style={{ padding: '24px', maxWidth: 960 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1E40AF', marginBottom: 4 }}>Impostazioni</h1>
      <p style={{ color: '#64748B', fontSize: 14, marginBottom: 24 }}>
        Personalizza loghi, notifiche, email SMTP e backup dell'applicazione.
      </p>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {TABS.map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)}
            className={clsx('px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              tab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            )}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'generale'  && <GeneraleSection />}
      {tab === 'smtp'      && <SmtpSection />}
      {tab === 'backup'    && <BackupSection />}
      {tab === 'commesse'  && <CommesseSection />}
      {tab === 'red'       && <RedLicenzaSection />}
      {tab === 'sicurezza' && (
        <div className="space-y-6">
          <Deroga2faSection />
          <BloccoAccessoNotturnoSection />
        </div>
      )}
    </div>
  )
}


interface UtenteDeroga2fa { id: number; nome: string | null; cognome: string | null; role: string; totp_enabled: boolean }

function Deroga2faSection() {
  const [stato, setStato] = useState<{ user_ids: number[]; puo_gestire: boolean } | null>(null)
  const [utenti, setUtenti] = useState<UtenteDeroga2fa[]>([])
  const [selezionati, setSelezionati] = useState<Set<number>>(new Set())

  useEffect(() => {
    configApi.get2faDerogaTecnici().then(r => {
      setStato(r.data)
      setSelezionati(new Set(r.data.user_ids))
    }).catch(() => {})
    authApi.listUsers().then(r => {
      setUtenti(r.data as UtenteDeroga2fa[])
    }).catch(() => {})
  }, [])

  const mut = useMutation({
    mutationFn: (userIds: number[]) => configApi.set2faDerogaTecnici(userIds),
    onSuccess: () => toast.success('Elenco deroghe aggiornato'),
    onError: () => toast.error('Errore nell\'aggiornamento'),
  })

  const toggle = (id: number) => setSelezionati(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const modificato = stato && (
    selezionati.size !== stato.user_ids.length ||
    stato.user_ids.some(id => !selezionati.has(id))
  )

  return (
    <div className="card p-6 max-w-xl space-y-4">
      <div>
        <h2 className="text-base font-semibold text-slate-800">Obbligo 2FA — deroga per utente</h2>
        <p className="text-sm text-slate-500 mt-1">
          Scegli quali utenti (qualsiasi ruolo) sono esentati temporaneamente dal popup obbligatorio
          di attivazione 2FA al login. Gli utenti non selezionati restano soggetti all'obbligo.
        </p>
      </div>
      {stato === null ? (
        <p className="text-sm text-slate-400">Caricamento...</p>
      ) : !stato.puo_gestire ? (
        <p className="text-xs text-slate-400">Solo un superadmin può modificare questa impostazione.</p>
      ) : (
        <>
          <div className="space-y-1 max-h-72 overflow-auto border border-slate-100 rounded-lg p-2">
            {utenti.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Nessun utente trovato.</p>
            ) : (
              utenti.map(t => (
                <label key={t.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                  <input type="checkbox" checked={selezionati.has(t.id)} onChange={() => toggle(t.id)} />
                  <span>{t.nome} {t.cognome}</span>
                  <span className="text-xs text-slate-400">({t.role})</span>
                  {!t.totp_enabled && <span className="text-xs text-slate-400">· 2FA non ancora attiva</span>}
                </label>
              ))
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="badge bg-amber-100 text-amber-700">{selezionati.size} esentati</span>
            <button
              className="btn-primary"
              disabled={!modificato || mut.isPending}
              onClick={() => { const ids = Array.from(selezionati); mut.mutate(ids, { onSuccess: () => setStato(s => s && { ...s, user_ids: ids }) }) }}
            >
              Salva
            </button>
          </div>
        </>
      )}
    </div>
  )
}


const GIORNI_LABEL: { key: string; label: string }[] = [
  { key: 'lun', label: 'Lunedì' },
  { key: 'mar', label: 'Martedì' },
  { key: 'mer', label: 'Mercoledì' },
  { key: 'gio', label: 'Giovedì' },
  { key: 'ven', label: 'Venerdì' },
  { key: 'sab', label: 'Sabato' },
  { key: 'dom', label: 'Domenica' },
]

interface GiornoBlocco { attivo: boolean; da: string; a: string }
interface BloccoAccessoConfig { abilitato: boolean; giorni: Record<string, GiornoBlocco> }

function BloccoAccessoNotturnoSection() {
  const { user } = useAuth()
  const isSuperadmin = (user as { role?: string } | null)?.role === 'superadmin'
  const [cfg, setCfg] = useState<BloccoAccessoConfig | null>(null)

  useEffect(() => {
    if (!isSuperadmin) return
    configApi.getBloccoAccessoNotturno().then(r => setCfg(r.data)).catch(() => {})
  }, [isSuperadmin])

  const mut = useMutation({
    mutationFn: (data: BloccoAccessoConfig) => configApi.setBloccoAccessoNotturno(data),
    onSuccess: () => toast.success('Blocco accesso notturno aggiornato'),
    onError: () => toast.error('Errore nell\'aggiornamento'),
  })

  if (!isSuperadmin) {
    return (
      <div className="card p-6 max-w-2xl">
        <h2 className="text-base font-semibold text-slate-800">Blocco accesso notturno</h2>
        <p className="text-xs text-slate-400 mt-2">Solo un superadmin può modificare questa impostazione.</p>
      </div>
    )
  }

  if (!cfg) {
    return (
      <div className="card p-6 max-w-2xl">
        <p className="text-sm text-slate-400">Caricamento...</p>
      </div>
    )
  }

  const setGiorno = (key: string, patch: Partial<GiornoBlocco>) => {
    setCfg(prev => prev && { ...prev, giorni: { ...prev.giorni, [key]: { ...prev.giorni[key], ...patch } } })
  }

  return (
    <div className="card p-6 max-w-2xl space-y-4">
      <div>
        <h2 className="text-base font-semibold text-slate-800">Blocco accesso notturno</h2>
        <p className="text-sm text-slate-500 mt-1">
          Impedisce il login a tutti gli utenti (superadmin sempre escluso) nelle fasce orarie
          configurate per ciascun giorno della settimana.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
        <input
          type="checkbox"
          checked={cfg.abilitato}
          onChange={e => setCfg(prev => prev && { ...prev, abilitato: e.target.checked })}
        />
        Abilita blocco accesso notturno
      </label>

      <div className="border border-slate-100 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs text-slate-400 uppercase">
              <th className="text-left px-3 py-2">Giorno</th>
              <th className="text-left px-3 py-2">Attivo</th>
              <th className="text-left px-3 py-2">Dalle</th>
              <th className="text-left px-3 py-2">Alle</th>
            </tr>
          </thead>
          <tbody>
            {GIORNI_LABEL.map(({ key, label }) => {
              const g = cfg.giorni[key] ?? { attivo: false, da: '21:00', a: '05:00' }
              return (
                <tr key={key} className="border-t border-slate-100">
                  <td className="px-3 py-2">{label}</td>
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={g.attivo} onChange={e => setGiorno(key, { attivo: e.target.checked })} />
                  </td>
                  <td className="px-3 py-2">
                    <input type="time" className="input" style={{ padding: '4px 8px' }} value={g.da}
                      onChange={e => setGiorno(key, { da: e.target.value })} disabled={!g.attivo} />
                  </td>
                  <td className="px-3 py-2">
                    <input type="time" className="input" style={{ padding: '4px 8px' }} value={g.a}
                      onChange={e => setGiorno(key, { a: e.target.value })} disabled={!g.attivo} />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <button className="btn-primary" disabled={mut.isPending} onClick={() => cfg && mut.mutate(cfg)}>
        Salva
      </button>
    </div>
  )
}


function RedLicenzaSection() {
  const [bloccato, setBloccato] = useState<boolean | null>(null)

  useEffect(() => {
    configApi.getRedLicenza().then(r => setBloccato(r.data.bloccato)).catch(() => {})
  }, [])

  const mut = useMutation({
    mutationFn: (nuovoValore: boolean) => configApi.setRedLicenza(nuovoValore),
    onSuccess: (_r, nuovoValore) => {
      setBloccato(nuovoValore)
      toast.success(nuovoValore ? 'Modulo RED bloccato' : 'Blocco rimosso, modulo RED sbloccato')
    },
    onError: () => toast.error('Errore nell\'aggiornamento'),
  })

  return (
    <div className="card p-6 max-w-xl space-y-4">
      <div>
        <h2 className="text-base font-semibold text-slate-800">Blocco licenze API</h2>
        <p className="text-sm text-slate-500 mt-1">
          Se le licenze API di terze parti usate dal modulo RED risultano scadute,
          l'accesso a tutte le rotte /api/red/* viene bloccato (errore 403) finché
          non rimuovi il blocco qui.
        </p>
      </div>
      {bloccato === null ? (
        <p className="text-sm text-slate-400">Caricamento...</p>
      ) : (
        <div className="flex items-center gap-3">
          <span className={clsx('badge', bloccato ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700')}>
            {bloccato ? 'Bloccato' : 'Sbloccato'}
          </span>
          {bloccato ? (
            <button className="btn-primary" disabled={mut.isPending} onClick={() => mut.mutate(false)}>
              Rimuovi blocco
            </button>
          ) : (
            <button className="btn-secondary" disabled={mut.isPending} onClick={() => mut.mutate(true)}>
              Blocca modulo RED
            </button>
          )}
        </div>
      )}
    </div>
  )
}
